import { useEffect } from 'react';

import { measureScrollOverflowEdges } from 'js/src/libs/useScrollOverflowEdges';

// Firefox reports line-based deltas for a plain mouse wheel; this is roughly
// one text line in pixels.
const LINE_HEIGHT_PX = 16;

const wheelDeltaInPixels = (event: WheelEvent): number => {
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    return event.deltaMode === WheelEvent.DOM_DELTA_LINE ? delta * LINE_HEIGHT_PX : delta;
};

/**
 * Lets a vertical mouse wheel scroll a horizontally overflowing element
 * sideways. A mouse has no horizontal gesture of its own, so without this a
 * mouse-only user can only reach the overflow by dragging a scrollbar, which
 * many horizontal scrollers hide. The wheel is only claimed while the element
 * can still move in that direction; otherwise the event is left alone so the
 * page scrolls as usual.
 */
export const useHorizontalWheelScroll = (ref: React.RefObject<HTMLElement | null>): void => {
    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        const handleWheel = (event: WheelEvent) => {
            const delta = wheelDeltaInPixels(event);
            if (delta === 0) {
                return;
            }
            // Same measurement (and sub-pixel tolerance) as the fade, so the
            // wheel is never claimed for an edge the fade already treats as reached.
            const overflow = measureScrollOverflowEdges(element);
            const canScrollThatWay = delta > 0 ? overflow.end : overflow.start;
            if (!canScrollThatWay) {
                return;
            }
            event.preventDefault();
            element.scrollLeft += delta;
        };

        element.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            element.removeEventListener('wheel', handleWheel);
        };
    }, [ref]);
};
